import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * SpaceCanvas — draws EVERYTHING on one canvas via requestAnimationFrame.
 *
 * What moves at all times (regardless of scroll):
 *  ⬡  200 stars — twinkling (opacity oscillation)
 *  ◉  22 asteroids — slow irregular drift across the screen, wrap around edges
 *  ✦  8 comets — zip across at an angle, leaving glowing tail, repeat on loop
 *  ☁  6 nebula wisps — large blurry blobs that drift and breathe (canvas, no DOM)
 */

const SpaceCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const PR = window.devicePixelRatio || 1;

    let W, H;
    const setSize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * PR;
      canvas.height = H * PR;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(PR, 0, 0, PR, 0, 0);
    };
    setSize();
    window.addEventListener('resize', setSize);

    // ── Stars ────────────────────────────────────────────────────────────────
    const STAR_COUNT = 200;
    const stars = Array.from({ length: STAR_COUNT }, () => {
      const r = Math.random() * 1.8 + 0.3;
      return {
        rx: Math.random(),
        ry: Math.random(),
        r,
        baseAlpha: Math.random() * 0.65 + 0.2,
        freq: Math.random() * 0.7 + 0.15,
        phase: Math.random() * Math.PI * 2,
        isPurple: r > 1.3 && Math.random() > 0.45,
      };
    });

    // ── Asteroids — slow drifting rocks ─────────────────────────────────────
    const ASTEROID_COUNT = 22;
    const asteroids = Array.from({ length: ASTEROID_COUNT }, () => {
      const size = Math.random() * 3.5 + 1;
      return {
        rx: Math.random(),
        ry: Math.random(),
        vx: (Math.random() - 0.5) * 0.28,   // very slow
        vy: (Math.random() - 0.5) * 0.18,
        size,
        alpha: Math.random() * 0.45 + 0.15,
        trail: [],          // stores last N positions for tail
        trailLen: Math.floor(Math.random() * 8 + 4),
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 0.004,
        sides: Math.floor(Math.random() * 3) + 5,  // pentagon–heptagon
      };
    });

    // ── Comets — fast streaks that zip across and respawn ───────────────────
    const COMET_COUNT = 6;
    const makeComet = () => ({
      x: Math.random() * W * 1.2 - W * 0.1,
      y: -40,
      vx: (Math.random() - 0.3) * 3.5,
      vy: Math.random() * 2.5 + 1.5,
      len: Math.random() * 90 + 60,
      alpha: Math.random() * 0.55 + 0.3,
      color: Math.random() > 0.5 ? [139, 92, 246] : [196, 181, 253],
      done: false,
    });
    const comets = Array.from({ length: COMET_COUNT }, (_, i) => {
      const c = makeComet();
      // Spread initial positions so they don't all arrive at once
      c.y = -40 - i * 280;
      c.x = Math.random() * W;
      return c;
    });

    // ── Nebula wisps — big soft blobs drifting on canvas ────────────────────
    const WISPS = [
      { x: 0.12, y: 0.08, r: 260, col: [109, 40, 217], a: 0.14, vx: 0.08, vy: 0.04, freq: 0.0008 },
      { x: 0.82, y: 0.12, r: 240, col: [99, 102, 241], a: 0.11, vx: -0.06, vy: 0.05, freq: 0.0012 },
      { x: 0.50, y: 0.04, r: 300, col: [139, 92, 246], a: 0.10, vx: 0.04, vy: 0.06, freq: 0.0006 },
      { x: 0.22, y: 0.65, r: 220, col: [167, 139, 250], a: 0.09, vx: 0.07, vy: -0.04, freq: 0.0010 },
      { x: 0.78, y: 0.58, r: 210, col: [79, 70, 229], a: 0.10, vx: -0.05, vy: -0.05, freq: 0.0009 },
      { x: 0.45, y: 0.88, r: 250, col: [139, 92, 246], a: 0.08, vx: 0.03, vy: -0.06, freq: 0.0007 },
    ].map(w => ({ ...w, cx: w.x, cy: w.y, phase: Math.random() * Math.PI * 2 }));

    // ── Polygon helper for asteroids ─────────────────────────────────────────
    const drawPolygon = (x, y, r, sides, rot) => {
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const angle = rot + (i / sides) * Math.PI * 2;
        const jitter = 0.75 + Math.random() * 0.5;
        const px = x + Math.cos(angle) * r * jitter;
        const py = y + Math.sin(angle) * r * jitter;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    let t = 0;
    let raf;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      t += 0.016;

      // ── DOM-based Progress for Footer Convergence ──
      // This calculates convergence based on actual footer visibility, entirely
      // independent of screen size and total page height.
      let convergePct = 0;
      const footerElement = document.querySelector('[data-testid="footer-section"]');

      if (footerElement) {
        const top = footerElement.getBoundingClientRect().top;
        const startY = window.innerHeight;        // Footer edge hits bottom of window
        const endY = window.innerHeight * 0.20;     // Footer reaches top quarter of window
        const rawPct = (startY - top) / (startY - endY);
        convergePct = Math.max(0, Math.min(1, rawPct));
      }

      const easeConverge = convergePct * convergePct * (3 - 2 * convergePct); // smoothstep

      const cx = W / 2;
      const cy = H / 2;

      // ── Draw nebula wisps ──────────────────────────────────────────────────
      for (const w of WISPS) {
        // Drift slowly, wrap
        w.cx += w.vx / W;
        w.cy += w.vy / H;
        if (w.cx < -0.15) w.cx = 1.15;
        if (w.cx > 1.15) w.cx = -0.15;
        if (w.cy < -0.15) w.cy = 1.15;
        if (w.cy > 1.15) w.cy = -0.15;

        // Converge towards center (0.5, 0.5)
        const targetCx = 0.5 + (w.cx - 0.5) * 0.2; // pull into 20% of screen
        const targetCy = 0.5 + (w.cy - 0.5) * 0.2;
        const drawCx = w.cx + (targetCx - w.cx) * easeConverge;
        const drawCy = w.cy + (targetCy - w.cy) * easeConverge;

        const breath = 0.65 + 0.35 * Math.sin(t * w.freq * 60 + w.phase);
        const alpha = w.a * breath * (1 - easeConverge * 0.3); // slightly fade out
        const px = drawCx * W;
        const py = drawCy * H;

        const grad = ctx.createRadialGradient(px, py, 0, px, py, w.r * (1 + breath * 0.25));
        grad.addColorStop(0, `rgba(${w.col[0]},${w.col[1]},${w.col[2]},${alpha})`);
        grad.addColorStop(0.45, `rgba(${w.col[0]},${w.col[1]},${w.col[2]},${alpha * 0.45})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.arc(px, py, w.r * (1 + breath * 0.25), 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      // ── Draw stars ────────────────────────────────────────────────────────
      for (const s of stars) {
        const absX = s.rx * W;
        const absY = s.ry * H;
        const drawX = absX + (cx - absX) * easeConverge * 0.85;
        const drawY = absY + (cy - absY) * easeConverge * 0.85;

        const alpha = Math.max(0.04, s.baseAlpha * (0.55 + 0.45 * Math.sin(t * s.freq + s.phase)));

        if (s.r > 1.3) {
          // Halo
          const g = ctx.createRadialGradient(drawX, drawY, 0, drawX, drawY, s.r * 4);
          const col = s.isPurple ? `${Math.round(alpha * 255)})` : null;
          g.addColorStop(0, s.isPurple
            ? `rgba(200,175,255,${alpha})`
            : `rgba(255,255,255,${alpha})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.arc(drawX, drawY, s.r * 4, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(drawX, drawY, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.isPurple
          ? `rgba(196,170,255,${Math.min(1, alpha * 1.3)})`
          : `rgba(255,255,255,${Math.min(1, alpha)})`;
        ctx.fill();
      }

      // ── Draw asteroids ────────────────────────────────────────────────────
      for (const a of asteroids) {
        // Move
        a.rx += a.vx / W;
        a.ry += a.vy / H;
        a.rot += a.rotV * (1 - easeConverge); // Slow down rotation

        // Wrap around edges with margin
        if (a.rx < -0.1) a.rx = 1.1;
        if (a.rx > 1.1) a.rx = -0.1;
        if (a.ry < -0.1) a.ry = 1.1;
        if (a.ry > 1.1) a.ry = -0.1;

        const absX = a.rx * W;
        const absY = a.ry * H;

        // Target position: drawn slowly into a tight circle around center
        const dx = cx - absX;
        const dy = cy - absY;
        const drawX = absX + dx * easeConverge * 0.9;
        const drawY = absY + dy * easeConverge * 0.9;

        // Trail
        a.trail.push({ x: drawX, y: drawY });
        if (a.trail.length > a.trailLen) a.trail.shift();

        // Draw trail
        if (a.trail.length > 1) {
          for (let i = 1; i < a.trail.length; i++) {
            const pct = i / a.trail.length;
            ctx.beginPath();
            ctx.moveTo(a.trail[i - 1].x, a.trail[i - 1].y);
            ctx.lineTo(a.trail[i].x, a.trail[i].y);
            ctx.strokeStyle = `rgba(180,160,255,${a.alpha * pct * 0.4})`;
            ctx.lineWidth = a.size * pct * 0.6;
            ctx.stroke();
          }
        }

        // Draw asteroid body
        ctx.save();
        ctx.translate(drawX, drawY);
        ctx.rotate(a.rot);
        drawPolygon(0, 0, a.size, a.sides, 0);
        ctx.fillStyle = `rgba(160,140,220,${a.alpha})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(200,180,255,${a.alpha * 0.7})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.restore();
      }

      // ── Draw comets ───────────────────────────────────────────────────────
      const cometFade = 1 - Math.min(1, easeConverge * 2); // Fade out twice as fast
      if (cometFade > 0.01) {
        for (const c of comets) {
          c.x += c.vx;
          c.y += c.vy;

          if (c.y > H + 60 || c.x < -200 || c.x > W + 200) {
            Object.assign(c, makeComet());
            continue;
          }

          const angle = Math.atan2(c.vy, c.vx);
          const tx = c.x - Math.cos(angle) * c.len;
          const ty = c.y - Math.sin(angle) * c.len;

          const glow = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 5);
          glow.addColorStop(0, `rgba(255,255,255,${c.alpha * cometFade})`);
          glow.addColorStop(0.4, `rgba(${c.color.join(',')},${c.alpha * 0.6 * cometFade})`);
          glow.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.arc(c.x, c.y, 5, 0, Math.PI * 2);
          ctx.fillStyle = glow;
          ctx.fill();

          const tail = ctx.createLinearGradient(c.x, c.y, tx, ty);
          tail.addColorStop(0, `rgba(${c.color.join(',')},${c.alpha * 0.8 * cometFade})`);
          tail.addColorStop(0.5, `rgba(${c.color.join(',')},${c.alpha * 0.3 * cometFade})`);
          tail.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.moveTo(c.x, c.y);
          ctx.lineTo(tx, ty);
          ctx.strokeStyle = tail;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', setSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

// ── Accretion disk rings (hero zone) — keep as motion.div for glow quality ───
const AccretionDisk = () => (
  <div className="absolute" style={{ left: '50%', top: '14%', transform: 'translate(-50%,-50%)' }}>
    {[
      { s: 1, d: 38, dir: 1, bw: 1.5, bo: 0.12 },
      { s: 0.74, d: 30, dir: -1, bw: 1.5, bo: 0.16 },
      { s: 0.54, d: 24, dir: 1, bw: 1, bo: 0.20 },
      { s: 0.37, d: 18, dir: -1, bw: 1, bo: 0.24 },
      { s: 0.22, d: 13, dir: 1, bw: 0.8, bo: 0.28 },
    ].map(({ s, d, dir, bw, bo }, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          width: 600 * s,
          height: 200 * s,
          left: '50%', top: '50%',
          transform: `translate(-50%,-50%) rotateX(76deg)`,
          border: `${bw}px solid rgba(139,92,246,${bo})`,
          boxShadow: `0 0 ${14 + i * 14}px rgba(139,92,246,${0.08 + i * 0.05}), inset 0 0 ${8 + i * 8}px rgba(139,92,246,${0.04 + i * 0.03})`,
          willChange: 'transform',
        }}
        animate={{ rotate: 360 * dir }}
        transition={{ duration: d, repeat: Infinity, ease: 'linear' }}
      />
    ))}
    {/* Breathing core */}
    <motion.div
      className="absolute rounded-full"
      style={{
        width: 180, height: 180,
        left: '50%', top: '50%',
        transform: 'translate(-50%,-50%)',
        background: 'radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(99,102,241,0.14) 40%, transparent 70%)',
        boxShadow: '0 0 100px rgba(139,92,246,0.22), 0 0 200px rgba(99,102,241,0.10)',
        filter: 'blur(3px)',
        willChange: 'transform, opacity',
      }}
      animate={{ scale: [1, 1.3, 0.95, 1.15, 1], opacity: [0.7, 1, 0.6, 0.9, 0.7] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    />
    {/* Singularity removed per user request */}
  </div>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const ParticlesBackground = () => (
  <div
    className="fixed inset-0 pointer-events-none overflow-hidden z-0"
    style={{ background: 'radial-gradient(ellipse at 50% 0%, #0d0820 0%, #05050a 40%, #020205 100%)' }}
  >
    {/* All stars, asteroids, comets, nebula wisps — one canvas, 60fps */}
    <SpaceCanvas />

    {/* Accretion rings — motion.div for crisp glow borders */}
    <AccretionDisk />

    {/* Dot grid — subtle star-map feel */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `radial-gradient(circle, rgba(139,92,246,0.18) 1px, transparent 1px)`,
        backgroundSize: '80px 80px',
        opacity: 0.16,
        pointerEvents: 'none',
      }}
    />

    {/* Top lens flare */}
    <div className="absolute" style={{
      left: '50%', top: 0,
      transform: 'translate(-50%, -35%)',
      width: 900, height: 450,
      background: 'radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, rgba(79,70,229,0.07) 45%, transparent 70%)',
      filter: 'blur(50px)',
    }} />
  </div>
);

export default ParticlesBackground;
